import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import * as yup from "yup";

import {
  CreateComplianceInviteMutation,
  CreateComplianceInviteMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect, FormikTextField } from "~frontend/components/forms";
import {
  CREATE_COMPLIANCE_INVITE,
  LIST_COMPLIANCE_INVITES,
} from "~frontend/graphql/complianceRegistrationInviteTokens";
import useMutation from "~frontend/util/useMutation";

const ComplianceInviteSchema = yup.object().shape({
  proposedTenantSlug: yup
    .string()
    .required("Proposed Tenant Slug is required")
    .matches(
      /^[a-z0-9-]*$/,
      "Can only contain lowercase letters, digits, and dashes"
    ),
  registrationType: yup
    .string()
    .oneOf(["TOLL_FREE", "TEN_DLC"])
    .required("Registration Type is required"),
});

export const CreateInviteModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): ReactElement => {
  const [createInvite, { loading }] = useMutation<
    CreateComplianceInviteMutation,
    CreateComplianceInviteMutationVariables
  >(CREATE_COMPLIANCE_INVITE, {
    refetchQueries: [LIST_COMPLIANCE_INVITES],
    awaitRefetchQueries: true,
  });

  const snackbar = useSnackbar();

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        initialValues={{
          proposedTenantSlug: "",
          registrationType: "",
        }}
        validationSchema={ComplianceInviteSchema}
        onSubmit={async values => {
          const { errors, data } = await createInvite({
            variables: {
              input: {
                proposedTenantSlug: values.proposedTenantSlug,
                registrationType: values.registrationType,
              },
            },
          });

          if (errors) {
            snackbar.enqueueSnackbar(
              `Error creating compliance registration invite: ${errors[0]?.message || "Unknown error"}`,
              {
                variant: "error",
              }
            );
          }

          if (data) {
            await navigator.clipboard.writeText(
              `${window.location.origin}/compliance/register/${values.registrationType.toLowerCase().replace("_", "-")}?slug=${values.proposedTenantSlug}&token=${data.createComplianceInvite.token}`
            );
            snackbar.enqueueSnackbar(
              "Invite created, registration link copied to clipboard",
              {
                variant: "success",
              }
            );
            onClose();
          }
        }}
      >
        {({ submitForm }) => (
          <>
            <DialogTitle>Create Compliance Registration Invite</DialogTitle>
            <DialogContent>
              <FlexBox direction="column" gap={1}>
                <FormikTextField
                  name="proposedTenantSlug"
                  label="Proposed Tenant Slug"
                />

                <FormikSelect
                  name="registrationType"
                  label="Registration Type"
                  withNoneOption
                >
                  <MenuItem value="TEN_DLC">10DLC</MenuItem>
                  <MenuItem value="TOLL_FREE">Toll-Free</MenuItem>
                </FormikSelect>
              </FlexBox>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => onClose()}>Cancel</Button>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                loading={loading}
                onClick={submitForm}
              >
                Create
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};
